/**
 * Centrale helper voor MSP-logo delivery.
 *
 * Alleen Storage-URL's van onze eigen Supabase-host (exacte hostname-match)
 * worden herschreven naar het image-transform endpoint. Externe, relatieve,
 * lege of onbekende URL's blijven letterlijk ongewijzigd.
 */

const OBJECT_PUBLIC_SEGMENT = '/storage/v1/object/public/';
const RENDER_PUBLIC_SEGMENT = '/storage/v1/render/image/public/';

/** Exacte hostname van onze eigen Supabase-instantie (trusted origin). */
export function getTrustedStorageHostname(): string | null {
  const raw = import.meta.env.VITE_SUPABASE_URL as string | undefined;
  if (!raw) return null;
  try {
    return new URL(raw).hostname;
  } catch {
    return null;
  }
}

export interface MspLogoOptions {
  /** Intrinsieke breedte/hoogte in px van de gevraagde variant. */
  size?: number;
  /** JPEG/WebP kwaliteit (20-100). */
  quality?: number;
}

/** Presets per weergavecontext. */
export const MSP_LOGO_PRESETS = {
  /** Lijst-/cardweergave (±40-64 px display, retina-proof). */
  card: { size: 128, quality: 70 },
  /** Detailweergave (±96 px display, retina-proof). */
  detail: { size: 256, quality: 80 },
} as const;

/**
 * Geeft een geoptimaliseerde logo-URL terug wanneer dat veilig kan,
 * anders de originele waarde ongewijzigd.
 */
export function getOptimizedMspLogoUrl(
  url: string | null | undefined,
  options: MspLogoOptions = {},
): string | null | undefined {
  if (!url || typeof url !== 'string') return url;

  const trustedHost = getTrustedStorageHostname();
  if (!trustedHost) return url;

  let parsed: URL;
  try {
    parsed = new URL(url);
  } catch {
    // Relatieve of ongeldige URL: laat ongemoeid.
    return url;
  }

  if (parsed.protocol !== 'https:' && parsed.protocol !== 'http:') return url;
  if (parsed.hostname !== trustedHost) return url;
  if (!parsed.pathname.startsWith(OBJECT_PUBLIC_SEGMENT)) return url;

  const size = Math.max(16, Math.round(options.size ?? MSP_LOGO_PRESETS.card.size));
  const quality = Math.min(100, Math.max(20, Math.round(options.quality ?? MSP_LOGO_PRESETS.card.quality)));

  const transformed = new URL(parsed.toString());
  transformed.pathname = parsed.pathname.replace(OBJECT_PUBLIC_SEGMENT, RENDER_PUBLIC_SEGMENT);
  // Bestaande query-parameters bewust volledig vervangen zodat er niets dubbel wordt opgebouwd.
  transformed.search = '';
  transformed.hash = '';
  transformed.searchParams.set('width', String(size));
  transformed.searchParams.set('height', String(size));
  transformed.searchParams.set('resize', 'contain');
  transformed.searchParams.set('quality', String(quality));

  return transformed.toString();
}

/** Kortere helpers voor de twee standaardcontexten. */
export function getCardLogoUrl(url: string | null | undefined) {
  return getOptimizedMspLogoUrl(url, MSP_LOGO_PRESETS.card);
}

export function getDetailLogoUrl(url: string | null | undefined) {
  return getOptimizedMspLogoUrl(url, MSP_LOGO_PRESETS.detail);
}
